import Vue from "vue";
import { AiseaApiFactory } from "@/api/aisea/apiFactory";
import isBoolean from "lodash/isBoolean";
import isObject from "lodash/isObject";
import isString from "lodash/isString";
import { OPERATIONS } from "@/config/const";
const { INPUT_TYPES: it, INPUT_TYPE_ENUM: ite, GROUP_TYPES: gt } = OPERATIONS;
const enterpriseFormApi = AiseaApiFactory.get("enterpriseForm");
import operationReportService from "@/services/operations/operationReportService";

/**
 * Service class for handling enterprise forms.
 */
class EnterpriseFormService {
  /**
   * APIで法人に紐づくForm一覧を取得する
   * @returns {Promise<Object|null>} 操作設定のオブジェクトまたはnull
   * @property {boolean} status - APIのリクエストが成功したかどうか
   * @property {number} code - レスポンスのステータスコード
   * @property {EnterpriseForm[]} data - レスポンスのデータ
   * @property {string} message - レスポンスのメッセージ
   * @throws {Error} - エラーが発生した場合、エラーをスローする
   */
  async getEnterpriseForms() {
    return await enterpriseFormApi.index();
  }

  validEnterpriseForms(enterpriseForms) {
    if (!Array.isArray(enterpriseForms)) return [];
    return enterpriseForms.filter(ef => {
      const isEnterpriseForm = this.isEnterpriseForm(ef);
      return isEnterpriseForm;
    });
  }

  enterpriseFormVersions(enterpriseForms) {
    return this.validEnterpriseForms(enterpriseForms)
      .map(ef => ef.form_versions)
      .flat()
      .filter(efv => {
        const isEnterpriseFormVersion = this.isEnterpriseFormVersion(efv);
        if (!isEnterpriseFormVersion) {
          Vue.$log.warn("Invalid EnterpriseFormVersion", efv);
        }
        return isEnterpriseFormVersion;
      });
  }

  enterpriseFormVersionItems(enterpriseFormVersions) {
    if (!Array.isArray(enterpriseFormVersions)) return [];
    return enterpriseFormVersions
      .filter(efv => this.isEnterpriseFormVersion(efv))
      .map(ef => ef.form_version_items)
      .flat()
      .filter(eFVI => {
        const isEnterpriseFormVersionItem = this.isEnterpriseFormVersionItem(
          eFVI
        );
        return isEnterpriseFormVersionItem;
      });
  }

  /**
   * Check if an object is an EnterpriseForm
   * @param {any} obj - The object to check
   * @returns {boolean} - True if the object is an EnterpriseForm, false otherwise
   */
  isEnterpriseForm(obj) {
    if (!isObject(obj)) return false;
    return isString(obj.id) && Array.isArray(obj.form_versions);
  }

  /**
   * Check if an object is an EnterpriseFormVersion
   * @param {any} obj - The object to check
   * @returns {boolean} - True if the object is an EnterpriseFormVersion, false otherwise
   */
  isEnterpriseFormVersion(obj) {
    if (!isObject(obj)) return false;
    return (
      isString(obj.id) &&
      Number.isInteger(obj.version_no) &&
      isString(obj.start_at) &&
      Array.isArray(obj.form_version_items)
    );
  }

  /**
   * INPUT_TYPES.DATE_PICKER, // 1: DATE_PICKER (年月日)
   * Checks if the input type is a date picker.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form object.
   * @param {number} enterpriseFormVersionItem.input_type - The input type to check.
   * @returns {boolean} Returns true if the input type is a date picker, otherwise false.
   */
  isDatePickers(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.DATE_PICKER;
  }

  /**
   * Retrieves the input type from the given enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {string | undefined} - The input type of the form version item.
   */
  getInputTypeFromFormVersionItem(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    return ite[eFVI.input_type];
  }

  /**
   * INPUT_TYPES.TIME_PICKER, // 2: TIME_PICKER (時間)
   * Checks if the input type is a time picker.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form object.
   * @param {number} enterpriseFormVersionItem.input_type - The input type to check.
   * @returns {boolean} Returns true if the input type is a time picker, otherwise false.
   */
  isTimePickers(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.TIME_PICKER;
  }

  /*
   * INPUT_TYPES.DATETIME_PICKER, // 3: SPACE （レイアウト調整用）
   * Checks if the input type of the enterprise form is a space.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form object.
   * @param {number} enterpriseFormVersionItem.input_type - The input type to check.
   * @returns {boolean} - Returns true if the input type is a space, otherwise false.
   */
  isSpace(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.SPACE;
  }

  /*
   * INPUT_TYPES.DATETIME_PICKER, // 4: DATETIME_PICKER (日時)
   * Checks if the input type of the enterprise form is a date-time picker.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form object.
   * @param {number} enterpriseFormVersionItem.input_type - The input type to check.
   * @returns {boolean} - Returns true if the input type is a date-time picker, otherwise false.
   */
  isDateTimePickers(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.DATETIME_PICKER;
  }

  /**
   * Checks if the given enterprise form has a valid format.
   *
   * @param {object} enterpriseFormVersionItem - The enterprise form object to check.
   * @returns {boolean} - Returns true if the enterprise form has a valid format, otherwise returns false.
   */
  hasFormat(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    return (
      (this.isDatePickers(eFVI) ||
        this.isTimePickers(eFVI) ||
        this.isDateTimePickers(eFVI)) &&
      isString(eFVI.format)
    );
  }

  /**
   * INPUT_TYPES.SINGLE_IMAGE_FILE, // 12: SINGLE_IMAGE_FILE (1ファイル(画像)アップロード)
   * Checks if the given enterprise form version item is a single image file.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item to check.
   * @param {number} enterpriseFormVersionItem.input_type - The input type to check.
   * @returns {boolean} - Returns true if the enterprise form version item is a single image file, false otherwise.
   */
  isSingleImageFile(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.SINGLE_IMAGE_FILE;
  }

  //   INPUT_TYPES.SELECT, // 5: SELECT (選択項目)
  isSelect(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.SELECT;
  }

  //   INPUT_TYPES.CHECK_BOX, // 7: CHECK_BOX (チェックボックス)
  isCheckbox(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.CHECK_BOX;
  }

  //   INPUT_TYPES.RADIO_BUTTON, // 8: RADIO_BUTTON (ラジオボタン)
  isRadioButton(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.RADIO_BUTTON;
  }

  //   INPUT_TYPES.NUMBER, // 9: NUMBER (数値入力)
  isNumber(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.NUMBER;
  }

  //   INPUT_TYPES.SHORT_TEXT, // 10: SHORT_TEXT  (短いテキスト入力)
  isShortText(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.SHORT_TEXT;
  }

  //   INPUT_TYPES.LONG_TEXT, // 11: LONG_TEXT  (長いテキスト入力)
  isLongText(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;

    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType === it.LONG_TEXT;
  }

  // 14: DATE_RANGE_PICKER（日付範囲。value_json {start,end}）
  isDateRangePicker(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;
    return this.getInputTypeFromFormVersionItem(eFVI) === it.DATE_RANGE_PICKER;
  }

  // 15: DATE_MULTI_SELECT_FROM_RANGE（範囲内複数日。value_json [date,...]）
  isDateMultiSelect(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;
    return (
      this.getInputTypeFromFormVersionItem(eFVI) ===
      it.DATE_MULTI_SELECT_FROM_RANGE
    );
  }

  // 16: FLEXIBLE_TIME_PICKER（AM/PM/未定/詳細時刻。value_string）
  isFlexibleTime(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;
    return (
      this.getInputTypeFromFormVersionItem(eFVI) === it.FLEXIBLE_TIME_PICKER
    );
  }

  // 17: PORT_HIERARCHY_PICKER（港3階層。value_json・port/cargo マスタ要のため read-only）
  isPortHierarchyPicker(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;
    return (
      this.getInputTypeFromFormVersionItem(eFVI) === it.PORT_HIERARCHY_PICKER
    );
  }

  // 18: CARGO_PICKER（貨物3点。value_json・read-only）
  isCargoPicker(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;
    return this.getInputTypeFromFormVersionItem(eFVI) === it.CARGO_PICKER;
  }

  // 19: BINDING（配船タブの直接列。レポート値を持たない＝レポート画面では描画しない）
  isBinding(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;
    return this.getInputTypeFromFormVersionItem(eFVI) === it.BINDING;
  }

  // 値を value_json（構造化値）に格納する type か（14/15/17/18）
  isValueJsonType(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    return (
      this.isDateRangePicker(eFVI) ||
      this.isDateMultiSelect(eFVI) ||
      this.isPortHierarchyPicker(eFVI) ||
      this.isCargoPicker(eFVI)
    );
  }

  // レポート画面に描画する項目か
  isRenderableInReport(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!isObject(eFVI)) return false;
    const inputType = this.getInputTypeFromFormVersionItem(eFVI);
    return inputType !== it.SINGLE_OTHER_FILE && inputType !== it.BINDING;
  }

  // 項目単位の編集可否（form_version_items.editable）
  isItemEditable(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!isObject(eFVI)) return true;
    return eFVI.editable !== false;
  }

  /**
   * Returns the value type based on the given enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {string} The value type.
   * @throws {Error} If the value type is invalid.
   */
  getValueType(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    // 未使用 // 0: FIXED (固定値)
    if (this.isDatePickers(eFVI)) return "value_date"; // 1: DATE_PICKER (年月日)
    if (this.isTimePickers(eFVI)) return "value_time"; // 2: TIME_PICKER (時間)
    if (this.isSpace(eFVI)) return null; // 3: SPACE（レイアウト調整用）
    if (this.isDateTimePickers(eFVI)) return "value_date_time"; // 4: DATETIME_PICKER (日時)
    if (this.isSelect(eFVI)) return "value_string"; // 5: SELECT (選択項目)
    // 未使用 // 6: HIERARCHICAL_SELECT (２階層選択)
    if (this.isCheckbox(eFVI)) return "value_string"; // 7: CHECK_BOX (チェックボックス)
    if (this.isRadioButton(eFVI)) return "value_string"; // 8: RADIO_BUTTON (ラジオボタン)
    if (this.isNumber(eFVI)) return "value_number"; // 9: NUMBER (数値入力)
    if (this.isShortText(eFVI)) return "value_string"; // 10: SHORT_TEXT  (短いテキスト入力)
    if (this.isLongText(eFVI)) return "value_string"; // 11: LONG_TEXT  (長いテキスト入力)
    if (this.isSingleImageFile(eFVI)) return "value_files"; // 12: SINGLE_IMAGE_FILE (1ファイル(画像)アップロード)
    // 未使用 // 13: SINGLE_OTHER_FILE（1ファイル(画像以外)アップロード)
    if (this.isDateRangePicker(eFVI)) return "value_json"; // 14: DATE_RANGE_PICKER
    if (this.isDateMultiSelect(eFVI)) return "value_json"; // 15: DATE_MULTI_SELECT_FROM_RANGE
    if (this.isFlexibleTime(eFVI)) return "value_string"; // 16: FLEXIBLE_TIME_PICKER
    if (this.isPortHierarchyPicker(eFVI)) return "value_json"; // 17: PORT_HIERARCHY_PICKER（read-only）
    if (this.isCargoPicker(eFVI)) return "value_json"; // 18: CARGO_PICKER（read-only）
    // 19: BINDING はレポート値を持たない
    return null;
  }

  /**
   * Checks if the given enterprise form version item has a unit.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item to check.
   * @returns {boolean} - Returns true if the enterprise form version item has a unit, false otherwise.
   */
  hasUnit(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;
    return isString(eFVI.unit);
  }

  /**
   * Retrieves the unit from the given enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {string} The unit of the enterprise form version item, or an empty string if it doesn't have a unit.
   */
  getUnit(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.hasUnit(eFVI)) return "";
    return eFVI.unit;
  }

  /**
   * Checks if the given enterprise form version item has select options.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item to check.
   * @returns {boolean} - Returns true if the enterprise form version item has select options, false otherwise.
   */
  hasSelectOptions(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;
    return Array.isArray(eFVI.select_options) && eFVI.select_options.length > 0;
  }

  /**
   * Retrieves the select options from the given enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {Array} - The select options. EnterpriseFormVersionItemSelectOption
   */
  getSelectOptions(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.hasSelectOptions(eFVI)) return [];
    return eFVI.select_options;
  }

  /**
   * Retrieves the label from an enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {string} The label of the enterprise form version item.
   */
  getLabelFromEnterpriseFormVersionItem(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return "";
    return eFVI.label;
  }

  /**
   * Retrieves the 'required' property from the given enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item to retrieve the 'required' property from.
   * @returns {boolean} - The value of the 'required' property from the enterprise form version item.
   */
  getRequiredFromEnterpriseFormVersionItem(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return false;
    return eFVI.required;
  }

  /**
   * Retrieves the nullable label from an enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {string} The nullable label.
   */
  getNullableLabelFromEnterpriseFormVersionItem(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return "";
    return eFVI.nullable_label;
  }

  /**
   * Retrieves the default value from the given enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {string} The default value of the enterprise form version item.
   */
  getDefaultValue(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return "";
    return eFVI.default_value;
  }

  hasDefaultValue(enterpriseFormVersionItem) {
    const defaultValue = this.getDefaultValue(enterpriseFormVersionItem);
    const hasDefaultValue = isString(defaultValue) && defaultValue !== "";
    return hasDefaultValue;
  }

  /**
   * Retrieves the default value ID from the given enterprise form version item.
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @returns {string} The default value ID. // operationReportFormVersionItemのid
   */
  getDefaultValueId(enterpriseFormVersionItem) {
    const eFVI = enterpriseFormVersionItem;
    if (!this.isEnterpriseFormVersionItem(eFVI)) return "";
    return eFVI.default_value_id;
  }

  /**
   * Retrieves the default enterprise form version item by the default value ID from the given enterprise form version item.
   *
   * @param {Object} enterpriseFormVersionItem - The enterprise form version item.
   * @param {Object} reportFormVersionItemMap - The map of report form version object.
   * @returns {AoEnterpriseVesselReportFormVersionItem} The default report form version item.
   */
  getDefaultReportFormVersion(
    enterpriseFormVersionItem,
    reportFormVersionItemMap
  ) {
    const eFVI = enterpriseFormVersionItem;
    const rFVIMap = reportFormVersionItemMap;
    if (!isObject(rFVIMap)) return null;

    const defaultValueId = this.getDefaultValueId(eFVI);
    const defaultFormVersionItem = rFVIMap[defaultValueId];
    return defaultFormVersionItem;
  }

  getDefaultValueByDefaultValueId(
    enterpriseFormVersionItem,
    reportFormVersionItemMap
  ) {
    const eFVI = enterpriseFormVersionItem;
    const rFVIMap = reportFormVersionItemMap;
    const defaultFormVersionItem = this.getDefaultReportFormVersion(
      eFVI,
      rFVIMap
    );
    const defaultValue = operationReportService.getValueFromReportFormVersionItem(
      defaultFormVersionItem
    );
    return defaultValue;
  }

  // ===== FormVersionItemGroup 関連メソッド =====

  /**
   * FormVersion に form_version_item_groups が存在するか判定
   * @param {Object} formVersion - EnterpriseFormVersion
   * @returns {boolean}
   */
  hasFormVersionItemGroups(formVersion) {
    if (!isObject(formVersion)) return false;
    return (
      Array.isArray(formVersion.form_version_item_groups) &&
      formVersion.form_version_item_groups.length > 0
    );
  }

  /**
   * FormVersion から form_version_item_groups を取得（sort_order順）
   * @param {Object} formVersion - EnterpriseFormVersion
   * @returns {Array}
   */
  getFormVersionItemGroups(formVersion) {
    if (!this.hasFormVersionItemGroups(formVersion)) return [];
    return [...formVersion.form_version_item_groups].sort(
      (a, b) => a.sort_order - b.sort_order
    );
  }

  /**
   * グループが TABLE 型か判定
   * @param {Object} group - FormVersionItemGroup
   * @returns {boolean}
   */
  isTableGroup(group) {
    if (!isObject(group)) return false;
    return group.group_type === gt.TABLE;
  }

  /**
   * グループが NORMAL 型か判定
   * @param {Object} group - FormVersionItemGroup
   * @returns {boolean}
   */
  isNormalGroup(group) {
    if (!isObject(group)) return false;
    return group.group_type === gt.NORMAL;
  }

  /**
   * 可変行テーブルグループか（row_repeatable=true。配下 items は1行分のテンプレ列定義で、
   * 値側 repeat_index で行数が可変）。Phase3 の可変行描画で使用。
   * @param {Object} group - FormVersionItemGroup
   * @returns {boolean}
   */
  isRepeatableGroup(group) {
    if (!isObject(group)) return false;
    return group.row_repeatable === true;
  }

  /**
   * 可変行テーブルで「編集時」に行追加・削除を許可するか（form_version_item_groups.row_addable_on_edit）
   * @param {Object} group - FormVersionItemGroup
   * @returns {boolean}
   */
  isRowAddableOnEdit(group) {
    if (!isObject(group)) return true;
    return group.row_addable_on_edit !== false;
  }

  /**
   * 可変行テーブルで「作成時」に行追加・削除を許可するか（form_version_item_groups.row_addable_on_create）
   * @param {Object} group - FormVersionItemGroup
   * @returns {boolean}
   */
  isRowAddableOnCreate(group) {
    if (!isObject(group)) return true;
    return group.row_addable_on_create !== false;
  }

  /**
   * FormVersion の全 item をグループごとに整理して返す
   * @param {Object} formVersion - EnterpriseFormVersion
   * @returns {Array<{group: Object|null, items: Array}>}
   */
  getGroupedItems(formVersion) {
    if (!this.hasFormVersionItemGroups(formVersion)) return [];
    const groups = this.getFormVersionItemGroups(formVersion); // group.sort_order 昇順
    const allItems = formVersion.form_version_items || [];
    const groupIds = new Set(groups.map(group => group.id));

    // グループ単位のエントリ。グループ間の順序は group.sort_order を維持する
    const groupEntries = groups.map(group => {
      const items = allItems
        .filter(item => item.form_version_item_group_id === group.id)
        .sort((a, b) => a.sort_order - b.sort_order);
      const sortOrder = items.length
        ? Math.min(...items.map(item => item.sort_order))
        : group.sort_order;
      return { group, items, sortOrder };
    });

    // どのグループにも属さない項目（group_id が null / 孤立参照）を sort_order 昇順で取得
    const ungroupedItems = allItems
      .filter(
        item =>
          (item.form_version_item_group_id == null ||
            !groupIds.has(item.form_version_item_group_id)) &&
          !this.isSpace(item)
      )
      .sort((a, b) => a.sort_order - b.sort_order);

    // 未グループ項目が無ければグループ列をそのまま返す（既存フォームの並びを完全維持）
    if (ungroupedItems.length === 0) return groupEntries;

    // 未グループ項目を、その sort_order の位置でグループ列へ差し込む
    const merged = [];
    let g = 0;
    let u = 0;
    while (g < groupEntries.length || u < ungroupedItems.length) {
      const useGroup =
        u >= ungroupedItems.length ||
        (g < groupEntries.length &&
          groupEntries[g].sortOrder <= ungroupedItems[u].sort_order);
      if (useGroup) {
        merged.push(groupEntries[g]);
        g++;
      } else {
        merged.push({ group: null, items: [ungroupedItems[u]] });
        u++;
      }
    }
    return merged;
  }

  /**
   * TABLE グループの items を row_index / col_index で2次元配列に変換
   * @param {Array} items - グループに所属する FormVersionItem の配列
   * @returns {Array<Array>} rows[rowIndex][colIndex] = item
   */
  buildTableMatrix(items) {
    if (!Array.isArray(items) || items.length === 0) return [];
    const maxRow = Math.max(...items.map(i => i.row_index || 0));
    const maxCol = Math.max(...items.map(i => i.col_index || 0));
    const matrix = [];
    for (let r = 0; r <= maxRow; r++) {
      const row = [];
      for (let c = 0; c <= maxCol; c++) {
        row.push(null);
      }
      matrix.push(row);
    }
    items.forEach(item => {
      const r = item.row_index;
      const c = item.col_index;
      if (r != null && c != null) {
        matrix[r][c] = item;
      }
    });
    return matrix;
  }

  /**
   * Check if an object is an EnterpriseFormVersionItem
   * @param {any} obj - The object to check
   * @returns {boolean} - True if the object is an EnterpriseFormVersionItem, false otherwise
   */
  isEnterpriseFormVersionItem(obj) {
    if (!isObject(obj)) return false;
    const isEnterpriseFormVersionItem =
      isString(obj.id) &&
      isString(obj.label) &&
      Number.isInteger(obj.input_type) &&
      (isString(obj.format) || obj.format === null) &&
      (isString(obj.default_value) || obj.default_value === null) &&
      (isString(obj.default_value_id) || obj.default_value_id === null) &&
      (Array.isArray(obj.select_options) || obj.select_options === null) && // select_optionsの中身までは見ていない
      (isString(obj.unit) || obj.unit === null) &&
      (Number.isInteger(obj.caution_min) || obj.caution_min === null) &&
      (Number.isInteger(obj.warning_min) || obj.warning_min === null) &&
      isBoolean(obj.required) &&
      (isString(obj.nullable_label) || obj.nullable_label === null) &&
      Number.isInteger(obj.sort_order);
    if (!isEnterpriseFormVersionItem) {
      Vue.$log.warn("Invalid EnterpriseFormVersionItem", obj);
    }
    return isEnterpriseFormVersionItem;
  }
}

export default new EnterpriseFormService();
